import { Helmet } from "react-helmet";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "../components/ui/button";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "../components/ui/form";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { useState } from "react";

const formSchema = z.object({
  name: z.string().min(2, "Name is required"),
  email: z.string().email("Valid email required"),
  message: z.string().min(10, "Message must be at least 10 characters"),
});

type FormValues = z.infer<typeof formSchema>;

const Contact = () => {
  const [submitted, setSubmitted] = useState(false);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      email: "",
      message: "",
    },
  });

  const onSubmit = async (data: FormValues) => {
    // TODO: Integrate with your email API (e.g. EmailJS, Formspree, serverless function, etc.)
    // For now, show confirmation.
    setSubmitted(true);
    form.reset();
  };

  return (
      <>
        <Helmet>
          <title>Contact Us | SurpriseTable</title>
          <meta
              name="description"
              content="Contact the SurpriseTable team for support, partnership, or press inquiries. Get in touch via email or the contact form."
          />
        </Helmet>

        <section className="bg-white py-16 transition-colors duration-200 dark:bg-gray-900">
          <div className="container mx-auto max-w-2xl px-4">
            <h1 className="mb-8 font-serif text-4xl font-bold text-gray-800 dark:text-white text-center">
              Contact Us
            </h1>

            <div className="mb-12 text-center">
              <p className="text-lg text-gray-700 dark:text-gray-300">
                Questions, feedback, partnership, or press? We’re here for you.
              </p>
              <div className="mt-4 flex flex-col items-center space-y-1">

                <span>
                <b>Legal:</b>{" "}
                  <a
                      href="mailto:info@deviofy.com"
                      className="text-primary hover:underline"
                  >
                  info@deviofy.com
                </a>
              </span>
                <span>
                <b>Company:</b> Deviofy FZCO, DSO-THUB, Dubai Digital Park, Building A2, Dubai Silicon Oasis, Dubai, UAE.
              </span>
              </div>
            </div>

            <div className="rounded-xl bg-gray-50 p-8 shadow-lg dark:bg-gray-800">
              <h2 className="mb-6 text-2xl font-semibold text-gray-800 dark:text-white text-center">
                Send Us a Message
              </h2>
              {submitted ? (
                  <div className="text-center text-green-600 dark:text-green-400 mb-8">
                    Thank you! Your message has been sent. We’ll get back to you soon.
                  </div>
              ) : (
                  <Form {...form}>
                    <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                      <FormField
                          control={form.control}
                          name="name"
                          render={({ field }) => (
                              <FormItem>
                                <FormLabel>Your Name</FormLabel>
                                <FormControl>
                                  <Input placeholder="Full Name" {...field} />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                          )}
                      />
                      <FormField
                          control={form.control}
                          name="email"
                          render={({ field }) => (
                              <FormItem>
                                <FormLabel>Email Address</FormLabel>
                                <FormControl>
                                  <Input type="email" placeholder="your@email.com" {...field} />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                          )}
                      />
                      <FormField
                          control={form.control}
                          name="message"
                          render={({ field }) => (
                              <FormItem>
                                <FormLabel>Your Message</FormLabel>
                                <FormControl>
                                  <Textarea
                                      placeholder="Type your message..."
                                      rows={5}
                                      {...field}
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                          )}
                      />
                      <Button
                          type="submit"
                          className="w-full bg-primary py-3 text-lg font-medium text-white hover:bg-primary/90"
                      >
                        Send Message
                      </Button>
                    </form>
                  </Form>
              )}
            </div>

            {/* Optional: Add a map, office address, or FAQ link */}
            <div className="mt-12 text-center">
            <span className="text-gray-500 dark:text-gray-400 text-sm">
              Deviofy FZCO, DSO-THUB, Dubai Digital Park, Building A2, Dubai Silicon Oasis, Dubai, UAE.
            </span>
              {/* <div className="mt-4">
              <iframe
                title="SurpriseTable Location"
                src="https://www.openstreetmap.org/export/embed.html?bbox=8.6792,50.1109,8.6822,50.1129&amp;layer=mapnik"
                style={{ width: "100%", height: "200px", border: 0 }}
                allowFullScreen
              />
            </div> */}
            </div>
          </div>
        </section>
      </>
  );
};

export default Contact;
